import React from 'react'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Brush } from 'recharts'
import { useAtom } from 'jotai'
import type { ChartConfigIdType } from '@shared/constants.js'
import { themeAtom } from '../../atoms.js'
import { useChartData } from '../../hooks/useChartData.js'
import Card from '../Card.js'
import { getChartTheme } from './chartTheme.js'

type Props = {
  chartId: ChartConfigIdType
  title: string
}

const LINE_COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6']

export default function MultiLineChartCard({ chartId, title }: Props) {
  const [theme] = useAtom(themeAtom)
  const { data, loading, error } = useChartData(chartId)
  const t = getChartTheme(theme)

  if (loading) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-gray-400">loading...</div>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-red-400">加载失败</div>
      </Card>
    )
  }

  const rows = data.rows
  if (rows.length === 0) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-gray-400">数据为空</div>
      </Card>
    )
  }

  const dimensions = data.rowsMetadata.filter((m) => m.isDimension)
  const measures = data.rowsMetadata.filter((m) => !m.isDimension)
  const xKey = dimensions[0]?.name ?? Object.keys(rows[0] ?? {})[0]!
  const lineKeys = measures.map((m) => m.name)

  return (
    <Card title={title} className="basis-full">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.gridColor} />
          <XAxis dataKey={xKey} tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
          <YAxis tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
          <Tooltip
            contentStyle={t.tooltipContentStyle}
            labelStyle={t.tooltipLabelStyle}
            itemStyle={t.tooltipItemStyle}
            cursor={{ stroke: t.cursorColor }}
          />
          <Legend formatter={(value) => <span style={t.legendStyle}>{value}</span>} />
          {lineKeys.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={LINE_COLORS[i % LINE_COLORS.length]}
              dot={false}
              strokeWidth={2}
            />
          ))}
          <Brush
            dataKey={xKey}
            height={24}
            stroke="#3b82f6"
            fill={theme === 'dark' ? '#1f2937' : '#f9fafb'}
            travellerWidth={8}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  )
}
